import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCalendarWorkspaceRealtime } from '../../hooks/useCalendarWorkspaceRealtime';
import {
  CalendarRecurrenceFields,
  emptyRecurrenceForm,
  recurrenceFormFromRule,
  ruleFromRecurrenceForm,
  type RecurrenceFormState,
} from '../../components/calendar/CalendarRecurrenceFields';
import {
  calendarPersonLabel,
  calendarStatusLabel,
  createCalendarTask,
  DEFAULT_CALENDAR_SLUG,
  deleteCalendarTask,
  isCalendarTaskOverdue,
  listCalendarDirectory,
  listCalendarTasks,
  listThreadTasks,
  saveCalendarTaskEdit,
  sortThreadTasksByDate,
  threadRootTask,
  type CalendarSeriesEditScope,
} from '../../lib/calendarWorkspace';
import { recurrenceRuleLabel } from '../../lib/calendarRecurrence';
import type { CalendarTask } from '../../types/calendarWorkspace';

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(d.getDate() + n);
  return next;
}

function addYmd(day: string, n: number): string {
  return ymd(addDays(new Date(`${day}T12:00:00`), n));
}

/** Split sorted YYYY-MM-DD dates into consecutive runs (gaps break the run). */
function contiguousDateRuns(dates: string[]): { start: string; end: string }[] {
  const sorted = [...new Set(dates.map((d) => d.trim()).filter(Boolean))].sort();
  if (sorted.length === 0) return [];
  const runs: { start: string; end: string }[] = [];
  let start = sorted[0]!;
  let end = sorted[0]!;
  for (let i = 1; i < sorted.length; i++) {
    const day = sorted[i]!;
    if (day === addYmd(end, 1)) {
      end = day;
      continue;
    }
    runs.push({ start, end });
    start = day;
    end = day;
  }
  runs.push({ start, end });
  return runs;
}

function monthGrid(anchor: Date): Date[] {
  const first = startOfMonth(anchor);
  const start = new Date(first);
  // Week starts on Sunday.
  start.setDate(first.getDate() - first.getDay());

  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));

  const cells: Date[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    cells.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return cells;
}

function formatDayHeading(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  return d.toLocaleDateString('en-SG', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const WEEKDAYS_MONTH = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const WEEKDAYS_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function CalendarHomePage() {
  const { slug = DEFAULT_CALENDAR_SLUG } = useParams<{ slug: string }>();
  const { session } = useAuth();
  const email = session?.user?.email ?? '';
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [mode, setMode] = useState<'month' | 'week'>('month');
  const [tasks, setTasks] = useState<CalendarTask[]>([]);
  const [directory, setDirectory] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [focusDay, setFocusDay] = useState(() => ymd(new Date()));
  const [selected, setSelected] = useState<CalendarTask | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CalendarTask | null>(null);
  const [timeline, setTimeline] = useState<CalendarTask[]>([]);
  const [busy, setBusy] = useState(false);
  const [recurForm, setRecurForm] = useState<RecurrenceFormState>(emptyRecurrenceForm());
  const [seriesScope, setSeriesScope] = useState<CalendarSeriesEditScope>('this');

  const today = ymd(new Date());
  const cells = useMemo(() => monthGrid(cursor), [cursor]);

  const weekCells = useMemo(() => {
    if (mode !== 'week') return [];
    const ref = new Date(cursor);
    ref.setHours(0, 0, 0, 0);
    // Work week Mon–Sat (no Sunday).
    const day = ref.getDay(); // 0 = Sun
    const mondayOffset = day === 0 ? -6 : 1 - day;
    ref.setDate(ref.getDate() + mondayOffset);
    return Array.from({ length: 6 }, (_, i) => addDays(ref, i));
  }, [mode, cursor]);

  const weekdayLabels = mode === 'week' ? WEEKDAYS_WEEK : WEEKDAYS_MONTH;
  const dayCells = mode === 'month' ? cells : weekCells;
  const weekCount =
    mode === 'week' ? 1 : Math.max(1, Math.ceil(cells.length / 7));

  const personName = (value: string | null | undefined) =>
    calendarPersonLabel(value, directory);

  const load = async () => {
    setError(null);
    try {
      const [taskRows, dir] = await Promise.all([
        listCalendarTasks(slug),
        listCalendarDirectory().catch(() => new Map<string, string>()),
      ]);
      setTasks(taskRows);
      setDirectory(dir);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load calendar.');
    }
  };

  useEffect(() => {
    void load();
  }, [slug]);

  useCalendarWorkspaceRealtime({
    workspaceSlug: slug,
    onChange: () => {
      void load();
    },
  });

  useEffect(() => {
    if (!selected) {
      setTimeline([]);
      setDraft(null);
      setEditing(false);
      setRecurForm(emptyRecurrenceForm());
      return;
    }
    setDraft(selected);
    setRecurForm(recurrenceFormFromRule(selected.recurrence_rule));
    setSeriesScope('this');
    void listThreadTasks(selected.thread_id)
      .then(setTimeline)
      .catch(() => setTimeline([selected]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const tasksByDay = useMemo(() => {
    const map = new Map<string, CalendarTask[]>();
    for (const t of tasks) {
      const key = t.todo_date;
      const list = map.get(key) ?? [];
      list.push(t);
      map.set(key, list);
    }
    for (const [key, list] of map) {
      map.set(
        key,
        [...list].sort((a, b) => {
          const aDone = a.status === 'done' ? 1 : 0;
          const bDone = b.status === 'done' ? 1 : 0;
          if (aDone !== bDone) return aDone - bDone;
          return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
        }),
      );
    }
    return map;
  }, [tasks]);

  /**
   * Linked threads → continuous bars only for consecutive to-do dates.
   * Gaps break the bar into separate segments (single-day leftovers stay as chips).
   */
  const threadSpans = useMemo(() => {
    const byThread = new Map<string, CalendarTask[]>();
    for (const t of tasks) {
      const list = byThread.get(t.thread_id) ?? [];
      list.push(t);
      byThread.set(t.thread_id, list);
    }
    type Span = {
      spanId: string;
      threadId: string;
      start: string;
      end: string;
      title: string;
      status: CalendarTask['status'];
      nodeByDay: Map<string, CalendarTask>;
      representative: CalendarTask;
    };
    const spans: Span[] = [];
    for (const [threadId, list] of byThread) {
      const open = list.filter((t) => t.status !== 'done');
      if (open.length === 0) continue;
      const root = threadRootTask(list) ?? list[0]!;
      const nodeByDay = new Map<string, CalendarTask>();
      for (const t of open) {
        const day = String(t.todo_date ?? '').trim().slice(0, 10);
        if (!day) continue;
        const prev = nodeByDay.get(day);
        if (!prev || t.thread_order < prev.thread_order) nodeByDay.set(day, t);
      }
      const dates = [...nodeByDay.keys()].sort();
      const status: CalendarTask['status'] = open.some((t) => t.status === 'in_progress')
        ? 'in_progress'
        : 'todo';
      const title = root.title.replace(/\s—\sfollow-up$/i, '');
      for (const run of contiguousDateRuns(dates)) {
        // Only multi-day consecutive runs become span bars.
        if (run.start === run.end) continue;
        const runNodes = new Map<string, CalendarTask>();
        for (let day = run.start; day <= run.end; day = addYmd(day, 1)) {
          const node = nodeByDay.get(day);
          if (node) runNodes.set(day, node);
        }
        spans.push({
          spanId: `${threadId}:${run.start}:${run.end}`,
          threadId,
          start: run.start,
          end: run.end,
          title,
          status,
          nodeByDay: runNodes,
          representative: runNodes.get(run.start) ?? root,
        });
      }
    }
    return spans;
  }, [tasks]);

  /** Days covered by a span bar — those nodes are not also drawn as standalone chips. */
  const spanCoveredKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const span of threadSpans) {
      for (let day = span.start; day <= span.end; day = addYmd(day, 1)) {
        keys.add(`${span.threadId}:${day}`);
      }
    }
    return keys;
  }, [threadSpans]);

  /**
   * Stable lane per multi-day bar for its FULL date range (never split across layers).
   * Longer bars pack first into the lowest free lane; a bar only moves up when the
   * whole run fits without colliding another multi-day bar.
   */
  const spanLaneById = useMemo(() => {
    const spanDays = (start: string, end: string) => {
      let n = 0;
      for (let d = start; d <= end; d = addYmd(d, 1)) n += 1;
      return n;
    };
    const sorted = [...threadSpans].sort((a, b) => {
      const dur = spanDays(b.start, b.end) - spanDays(a.start, a.end);
      if (dur !== 0) return dur;
      if (a.start !== b.start) return a.start.localeCompare(b.start);
      if (a.end !== b.end) return a.end.localeCompare(b.end);
      return a.spanId.localeCompare(b.spanId);
    });
    const laneById = new Map<string, number>();
    const laneEnds: string[] = [];
    for (const span of sorted) {
      let lane = laneEnds.findIndex((end) => end < span.start);
      if (lane < 0) {
        lane = laneEnds.length;
        laneEnds.push(span.end);
      } else {
        laneEnds[lane] = span.end;
      }
      laneById.set(span.spanId, lane);
    }
    return laneById;
  }, [threadSpans]);

  const sortedTimeline = useMemo(() => sortThreadTasksByDate(timeline), [timeline]);

  const focusTasks = tasksByDay.get(focusDay) ?? [];
  const focusOpenTasks = useMemo(
    () => focusTasks.filter((t) => t.status !== 'done'),
    [focusTasks],
  );
  const focusDoneCount = focusTasks.length - focusOpenTasks.length;
  const monthLabel = cursor.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });

  const spanSegmentForDay = (day: string, start: string, end: string) => {
    if (day < start || day > end) return null;
    const d = new Date(`${day}T12:00:00`);
    const dow = d.getDay();
    const weekStartDow = mode === 'week' ? 1 : 0; // Mon (week) / Sun (month)
    const weekEndDow = 6; // Sat
    const atRangeStart = day === start;
    const atRangeEnd = day === end;
    const atWeekStart = dow === weekStartDow;
    const atWeekEnd = dow === weekEndDow;
    const isSegStart = atRangeStart || atWeekStart;
    const isSegEnd = atRangeEnd || atWeekEnd;
    const kind =
      isSegStart && isSegEnd ? 'only' : isSegStart ? 'start' : isSegEnd ? 'end' : 'mid';
    return { kind, showTitle: isSegStart };
  };

  const selectDay = (day: string) => {
    setFocusDay(day);
    setSelected(null);
    setEditing(false);
  };

  const openTask = (task: CalendarTask) => {
    setFocusDay(task.todo_date);
    setSelected(task);
    setEditing(false);
  };

  const quickAdd = async (day: string) => {
    if (!email) return;
    setBusy(true);
    setError(null);
    setFocusDay(day);
    try {
      const task = await createCalendarTask(
        slug,
        {
          title: 'New task',
          request_date: day,
          todo_date: day,
          deadline: day,
          assignee_email: email,
        },
        email,
      );
      await load();
      setSelected(task);
      setEditing(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create task.');
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = () => {
    setDraft(selected);
    setRecurForm(recurrenceFormFromRule(selected?.recurrence_rule));
    setEditing(false);
  };

  const saveSelected = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const withRecur = await saveCalendarTaskEdit({
        id: draft.id,
        patch: {
          title: draft.title,
          description: draft.description,
          status: draft.status,
          request_date: draft.request_date,
          todo_date: draft.todo_date,
          deadline: draft.deadline,
          assignee_email: draft.assignee_email,
        },
        recurrenceRule: ruleFromRecurrenceForm(recurForm),
        seriesScope: draft.recurrence_series_id ? seriesScope : 'this',
      });
      setSelected(withRecur);
      setDraft(withRecur);
      setRecurForm(recurrenceFormFromRule(withRecur.recurrence_rule));
      setSeriesScope('this');
      setEditing(false);
      setFocusDay(withRecur.todo_date);
      await load();
      setTimeline(await listThreadTasks(withRecur.thread_id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const addFollowUp = async () => {
    if (!selected || !email) return;
    setBusy(true);
    try {
      const base = new Date(`${selected.todo_date}T12:00:00`);
      base.setDate(base.getDate() + 2);
      const next = ymd(base);
      const first = threadRootTask(timeline) ?? selected;
      const task = await createCalendarTask(
        slug,
        {
          title: `${first.title.replace(/\s—\sfollow-up$/i, '')} — follow-up`,
          request_date: first.request_date,
          todo_date: next,
          deadline: first.deadline,
          assignee_email: selected.assignee_email || email,
          thread_id: selected.thread_id,
        },
        email,
      );
      await load();
      setFocusDay(next);
      setSelected(task);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add follow-up.');
    } finally {
      setBusy(false);
    }
  };

  const removeSelected = async () => {
    if (!selected) return;
    if (!confirm(`Delete “${selected.title}”?`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteCalendarTask(selected.id);
      setSelected(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const renderDay = (d: Date) => {
    const key = ymd(d);
    const inMonth = mode === 'week' || d.getMonth() === cursor.getMonth();
    const dayTasks = tasksByDay.get(key) ?? [];
    const isToday = key === today;
    const isFocused = key === focusDay;
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    const doneCount = dayTasks.filter((t) => t.status === 'done').length;
    const openStandalone = dayTasks.filter(
      (t) => t.status !== 'done' && !spanCoveredKeys.has(`${t.thread_id}:${key}`),
    );
    const daySpans = threadSpans.filter(
      (s) => s.status !== 'done' && key >= s.start && key <= s.end,
    );
    const spanByLane = new Map<number, (typeof threadSpans)[number]>();
    let maxLaneOnDay = -1;
    for (const span of daySpans) {
      const lane = spanLaneById.get(span.spanId) ?? 0;
      spanByLane.set(lane, span);
      if (lane > maxLaneOnDay) maxLaneOnDay = lane;
    }
    // Fixed lanes for continuity. Empty upper slots: single-day first, else blank.
    const dayLanes =
      maxLaneOnDay < 0 ? [] : Array.from({ length: maxLaneOnDay + 1 }, (_, i) => i);
    const holeLanes = dayLanes.filter((lane) => !spanByLane.has(lane));
    let standaloneCursor = 0;
    const holeFillByLane = new Map<number, CalendarTask | null>();
    for (const lane of holeLanes) {
      holeFillByLane.set(
        lane,
        standaloneCursor < openStandalone.length ? openStandalone[standaloneCursor++]! : null,
      );
    }
    const remainingStandalone = openStandalone.slice(standaloneCursor);
    const chipLimit = mode === 'week' ? 40 : 6;
    const laneSlots = dayLanes.length;
    const standaloneReserve =
      remainingStandalone.length === 0 ? 0 : Math.min(2, remainingStandalone.length);
    const belowBudget = Math.max(
      standaloneReserve,
      chipLimit - Math.min(laneSlots, Math.max(0, chipLimit - standaloneReserve)),
    );
    const visibleBelow = remainingStandalone.slice(0, belowBudget);
    const hiddenCount = Math.max(0, remainingStandalone.length - visibleBelow.length);
    const selectedDay = String(selected?.todo_date ?? '').trim().slice(0, 10);

    const renderStandaloneChip = (t: CalendarTask) => {
      const overdue = isCalendarTaskOverdue(t, today);
      return (
        <button
          type="button"
          key={t.id}
          className={`cal-chip cal-chip--task cal-chip--${t.status}${
            overdue ? ' is-overdue' : ''
          }${selected?.id === t.id ? ' is-selected' : ''}`}
          title={overdue ? `${t.title} (Overdue)` : t.title}
          onClick={(e) => {
            e.stopPropagation();
            openTask(t);
          }}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          {overdue ? `⚠ ${t.title}` : t.title}
        </button>
      );
    };

    const renderHole = (lane: number, fallbackKey: string) => {
      const fill = holeFillByLane.get(lane) ?? null;
      if (fill) return renderStandaloneChip(fill);
      return (
        <div
          key={fallbackKey}
          className="cal-chip cal-chip--span cal-chip--span-spacer"
          aria-hidden
        >
          &nbsp;
        </div>
      );
    };

    return (
      <div
        key={key}
        role="button"
        tabIndex={0}
        className={`cal-day${inMonth ? '' : ' is-outside'}${isToday ? ' is-today' : ''}${
          isFocused ? ' is-focused' : ''
        }${isWeekend ? ' is-weekend' : ''}`}
        title="Click to view · Double-click to add"
        onClick={() => selectDay(key)}
        onDoubleClick={(e) => {
          e.preventDefault();
          void quickAdd(key);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectDay(key);
          }
        }}
      >
        <div className="cal-day__heading">
          <span className="cal-day__num">{d.getDate()}</span>
          {doneCount > 0 ? (
            <span className="cal-day__done" title={`${doneCount} completed`}>
              <span className="cal-day__done-check" aria-hidden>
                ✓
              </span>
              <span className="cal-day__done-count">{doneCount}</span>
            </span>
          ) : null}
        </div>
        <div className="cal-day__events">
          {dayLanes.map((lane) => {
            const span = spanByLane.get(lane);
            if (!span) return renderHole(lane, `span-spacer-${lane}`);
            const seg = spanSegmentForDay(key, span.start, span.end);
            if (!seg) return renderHole(lane, `span-spacer-${span.spanId}`);
            const node = span.nodeByDay.get(key) ?? span.representative;
            const overdue = isCalendarTaskOverdue(node, today);
            const selectedInSpan = Boolean(
              selected &&
                selected.thread_id === span.threadId &&
                selectedDay &&
                selectedDay >= span.start &&
                selectedDay <= span.end,
            );
            return (
              <button
                type="button"
                key={`span-${span.spanId}`}
                className={`cal-chip cal-chip--span cal-chip--span-${seg.kind} cal-chip--${span.status}${
                  overdue ? ' is-overdue' : ''
                }${selectedInSpan ? ' is-selected' : ''}`}
                title={overdue ? `${span.title} (Overdue)` : span.title}
                onClick={(e) => {
                  e.stopPropagation();
                  openTask(node);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                {seg.showTitle ? (overdue ? `⚠ ${span.title}` : span.title) : '\u00a0'}
              </button>
            );
          })}
          {visibleBelow.map((t) => renderStandaloneChip(t))}
          {hiddenCount > 0 ? <span className="cal-day__more">+{hiddenCount}</span> : null}
        </div>
      </div>
    );
  };

  const view = draft ?? selected;

  return (
    <div className="cal-page cal-page--calendar">
      <div className="cal-toolbar">
        <div className="cal-toolbar__left">
          <button
            type="button"
            className="cal-tool-btn"
            onClick={() => {
              const now = new Date();
              setCursor(mode === 'week' ? now : startOfMonth(now));
              setFocusDay(ymd(now));
              setSelected(null);
            }}
          >
            Today
          </button>
          <div className="cal-tool-arrows">
            <button
              type="button"
              className="cal-tool-btn cal-tool-btn--icon"
              onClick={() =>
                setCursor((c) => (mode === 'week' ? addDays(c, -7) : addMonths(c, -1)))
              }
              aria-label={mode === 'week' ? 'Previous week' : 'Previous month'}
            >
              ‹
            </button>
            <button
              type="button"
              className="cal-tool-btn cal-tool-btn--icon"
              onClick={() =>
                setCursor((c) => (mode === 'week' ? addDays(c, 7) : addMonths(c, 1)))
              }
              aria-label={mode === 'week' ? 'Next week' : 'Next month'}
            >
              ›
            </button>
          </div>
          <h2 className="cal-toolbar__label">{monthLabel}</h2>
        </div>
        <div className="cal-seg">
          <button
            type="button"
            className={mode === 'month' ? 'is-active' : undefined}
            onClick={() => setMode('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={mode === 'week' ? 'is-active' : undefined}
            onClick={() => setMode('week')}
          >
            Week
          </button>
        </div>
      </div>

      {error ? <p className="alert alert-error">{error}</p> : null}

      <div className="cal-workspace">
        <div className="cal-grid-wrap">
          <div
            className={`cal-grid${mode === 'week' ? ' cal-grid--week' : ' cal-grid--month'}`}
            style={
              {
                '--cal-cols': String(weekdayLabels.length),
                '--cal-rows': String(weekCount),
              } as CSSProperties
            }
          >
            {weekdayLabels.map((w) => (
              <div
                key={w}
                className={`cal-weekday${w === 'Sun' || w === 'Sat' ? ' is-weekend' : ''}`}
              >
                {w}
              </div>
            ))}
            {dayCells.map((d) => renderDay(d))}
          </div>
        </div>

        <aside className="cal-day-panel" aria-label="Day details">
          {view ? (
            <div className="cal-day-panel__body">
              <div className="cal-day-panel__head cal-day-panel__head--detail">
                <button
                  type="button"
                  className="cal-link-btn"
                  onClick={() => {
                    setSelected(null);
                    setEditing(false);
                  }}
                >
                  ← {formatDayHeading(focusDay)}
                </button>
                <div className="cal-detail-head">
                  <h3>Detail</h3>
                  {!editing ? (
                    <button type="button" className="cal-link-btn" onClick={() => setEditing(true)}>
                      Edit
                    </button>
                  ) : (
                    <button type="button" className="cal-link-btn" onClick={cancelEdit}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {editing ? (
                <>
                  <label className="cal-field">
                    <span>Title</span>
                    <input
                      value={view.title}
                      onChange={(e) => setDraft({ ...view, title: e.target.value })}
                    />
                  </label>
                  <label className="cal-field">
                    <span>Description</span>
                    <textarea
                      rows={3}
                      value={view.description}
                      onChange={(e) => setDraft({ ...view, description: e.target.value })}
                    />
                  </label>
                  <div className="cal-fields-row cal-fields-row--stack">
                    <label className="cal-field">
                      <span>Request</span>
                      <input
                        type="date"
                        value={view.request_date}
                        onChange={(e) => setDraft({ ...view, request_date: e.target.value })}
                      />
                    </label>
                    <label className="cal-field">
                      <span>To-do</span>
                      <input
                        type="date"
                        value={view.todo_date}
                        onChange={(e) => setDraft({ ...view, todo_date: e.target.value })}
                      />
                    </label>
                    <label className="cal-field">
                      <span>Deadline</span>
                      <input
                        type="date"
                        value={view.deadline}
                        onChange={(e) => setDraft({ ...view, deadline: e.target.value })}
                      />
                    </label>
                  </div>
                  <div className="cal-fields-row cal-fields-row--stack">
                    <label className="cal-field">
                      <span>Status</span>
                      <select
                        value={view.status}
                        onChange={(e) =>
                          setDraft({
                            ...view,
                            status: e.target.value as CalendarTask['status'],
                          })
                        }
                      >
                        <option value="todo">To do</option>
                        <option value="in_progress">In progress</option>
                        <option value="done">Done</option>
                      </select>
                    </label>
                    <label className="cal-field">
                      <span>Assignee</span>
                      {directory.size > 0 ? (
                        <select
                          value={view.assignee_email}
                          onChange={(e) => setDraft({ ...view, assignee_email: e.target.value })}
                        >
                          {[...directory.entries()]
                            .sort((a, b) => a[1].localeCompare(b[1]))
                            .map(([addr, name]) => (
                              <option key={addr} value={addr}>
                                {name}
                              </option>
                            ))}
                          {view.assignee_email &&
                          !directory.has(view.assignee_email.toLowerCase()) ? (
                            <option value={view.assignee_email}>
                              {personName(view.assignee_email)}
                            </option>
                          ) : null}
                        </select>
                      ) : (
                        <input
                          value={view.assignee_email}
                          onChange={(e) => setDraft({ ...view, assignee_email: e.target.value })}
                        />
                      )}
                    </label>
                  </div>
                  <CalendarRecurrenceFields value={recurForm} onChange={setRecurForm} />
                  {view.recurrence_series_id ? (
                    <fieldset className="cal-series-scope">
                      <legend>Apply content changes to</legend>
                      <label className="cal-series-scope__option">
                        <input
                          type="radio"
                          name="cal-series-scope"
                          checked={seriesScope === 'this'}
                          onChange={() => setSeriesScope('this')}
                        />
                        This occurrence only
                      </label>
                      <label className="cal-series-scope__option">
                        <input
                          type="radio"
                          name="cal-series-scope"
                          checked={seriesScope === 'series'}
                          onChange={() => setSeriesScope('series')}
                        />
                        All occurrences
                      </label>
                      <p className="cal-field__hint">
                        Title, description, assignee, and date offsets follow this choice. Repeat
                        schedule / ending date always updates the whole series. Status stays on this
                        occurrence.
                      </p>
                    </fieldset>
                  ) : null}
                  <div className="cal-drawer__actions">
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      disabled={busy}
                      onClick={() => void saveSelected()}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busy}
                      onClick={() => void addFollowUp()}
                    >
                      Follow-up
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm cal-btn-danger"
                      disabled={busy}
                      onClick={() => void removeSelected()}
                    >
                      Delete
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="cal-readonly">
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Title</span>
                      <p className="cal-readonly__value">
                        {view.title}
                        {isCalendarTaskOverdue(view, today) ? (
                          <span className="cal-overdue-badge">Overdue</span>
                        ) : null}
                      </p>
                    </div>
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Description</span>
                      <p className="cal-readonly__value cal-readonly__value--pre">
                        {view.description.trim() || '—'}
                      </p>
                    </div>
                    <div className="cal-readonly__grid">
                      <div className="cal-readonly__block">
                        <span className="cal-readonly__label">Request</span>
                        <p className="cal-readonly__value">{view.request_date}</p>
                      </div>
                      <div className="cal-readonly__block">
                        <span className="cal-readonly__label">To-do</span>
                        <p className="cal-readonly__value">{view.todo_date}</p>
                      </div>
                      <div className="cal-readonly__block">
                        <span className="cal-readonly__label">Deadline</span>
                        <p
                          className={`cal-readonly__value${
                            isCalendarTaskOverdue(view, today) ? ' is-overdue-text' : ''
                          }`}
                        >
                          {view.deadline}
                        </p>
                      </div>
                    </div>
                    <div className="cal-readonly__grid">
                      <div className="cal-readonly__block">
                        <span className="cal-readonly__label">Status</span>
                        <p className="cal-readonly__value">{calendarStatusLabel(view.status)}</p>
                      </div>
                      <div className="cal-readonly__block">
                        <span className="cal-readonly__label">Assignee</span>
                        <p className="cal-readonly__value">
                          {personName(view.assignee_email) || '—'}
                        </p>
                      </div>
                    </div>
                    {view.recurrence_rule || view.recurrence_series_id ? (
                      <div className="cal-readonly__block">
                        <span className="cal-readonly__label">Repeats</span>
                        <p className="cal-readonly__value">
                          {recurrenceRuleLabel(view.recurrence_rule)}
                        </p>
                      </div>
                    ) : (
                      <div className="cal-readonly__block">
                        <span className="cal-readonly__label">Repeats</span>
                        <p className="cal-readonly__value">Does not repeat</p>
                      </div>
                    )}
                  </div>
                  <div className="cal-drawer__actions">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busy}
                      onClick={() => void addFollowUp()}
                    >
                      Follow-up
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm cal-btn-danger"
                      disabled={busy}
                      onClick={() => void removeSelected()}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}

              <h4 className="cal-timeline__title">Linked timeline</h4>
              <ol className="cal-timeline">
                {sortedTimeline.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`cal-timeline__item${t.id === view.id ? ' is-current' : ''}${
                        isCalendarTaskOverdue(t, today) ? ' is-overdue' : ''
                      }`}
                      onClick={() => openTask(t)}
                    >
                      <span className="cal-timeline__date">{t.todo_date}</span>
                      <span className="cal-timeline__name">
                        {t.title}
                        {isCalendarTaskOverdue(t, today) ? (
                          <span className="cal-overdue-badge">Overdue</span>
                        ) : null}
                      </span>
                      <span className="cal-timeline__meta">
                        {calendarStatusLabel(t.status)} · DL {t.deadline}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className="cal-day-panel__body">
              <div className="cal-day-panel__head">
                <p className="cal-day-panel__kicker">Selected day</p>
                <h3>{formatDayHeading(focusDay)}</h3>
              </div>

              <section className="cal-day-section">
                <div className="cal-day-section__title">
                  <h4>Tasks</h4>
                  <span>{focusOpenTasks.length}</span>
                </div>
                {focusDoneCount > 0 ? (
                  <p className="cal-day-done-summary">
                    <span className="cal-day__done-check" aria-hidden>
                      ✓
                    </span>
                    {focusDoneCount} completed
                  </p>
                ) : null}
                {focusOpenTasks.length === 0 ? (
                  <p className="cal-empty">
                    {focusDoneCount > 0 ? 'No open tasks on this day.' : 'No tasks on this day.'}
                  </p>
                ) : (
                  <ul className="cal-day-list">
                    {focusOpenTasks.map((t) => {
                      const overdue = isCalendarTaskOverdue(t, today);
                      return (
                        <li key={t.id}>
                          <button
                            type="button"
                            className={`cal-day-row cal-day-row--${t.status}${
                              overdue ? ' is-overdue' : ''
                            }`}
                            onClick={() => openTask(t)}
                          >
                            <strong>
                              {t.title}
                              {overdue ? <span className="cal-overdue-badge">Overdue</span> : null}
                              {t.recurrence_series_id ? (
                                <span className="cal-repeat-badge">Repeats</span>
                              ) : null}
                            </strong>
                            <span>
                              {calendarStatusLabel(t.status)} · DL {t.deadline}
                            </span>
                            {t.assignee_email ? (
                              <span className="cal-day-card__assignee">
                                {personName(t.assignee_email)}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <div className="cal-day-panel__foot">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy || !email}
                  onClick={() => void quickAdd(focusDay)}
                >
                  Add task
                </button>
                <p className="cal-hint">Or double-click a day on the calendar</p>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
